import { FC, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Oportunidade } from '@/types/oportunidade'
import { Produto } from '@/types/produto'
import {
  ReclassificacaoProduto,
  STATUS_RECLASSIFICAVEIS,
  TAMANHO_MAXIMO_MOTIVO_RECLASSIFICACAO,
} from '@/types/reclassificacao'
import { STATUS_LABEL, StatusReserva } from '@/types/statusReserva'
import { produtoService } from '@/services/produtoService'
import { mensagemErroReclassificacao, reclassificacaoService } from '@/services/reclassificacaoService'
import { CODIGO_OUTRA_NECESSIDADE } from '@/constants/codigosProduto'
import { categoriaRetirada } from '@/constants/categoriasProduto'
import { ROTULO_OUTRA_NECESSIDADE, reservaEhOutraNecessidade } from '@/utils/outraNecessidade'
import { formatDateTime } from '@/utils/formatters'

interface Props {
  oportunidade: Oportunidade
  /**
   * Back-office sem escopo de dono (ADMIN, GERENTE, OPERADOR — Q-PP1-2). O consultor só lê o
   * histórico; a ação nem aparece (o backend responde 403 de qualquer forma).
   */
  podeReclassificar: boolean
  onReclassificada: (atualizada: Oportunidade) => void
}

/** Destinos possíveis: ativos, não retirados e nunca a própria "Outra necessidade". */
const destinoValido = (p: Produto) =>
  p.ativo !== false &&
  p.status !== 'RETIRADA' &&
  !categoriaRetirada(p.categoria) &&
  p.codigo !== CODIGO_OUTRA_NECESSIDADE &&
  p.categoria !== 'OUTRA_NECESSIDADE'

/**
 * P1 (AN-PP1 §7): "Outra necessidade" reclassificada pelo back-office para um produto do
 * catálogo, com motivo, e o histórico das reclassificações da reserva. Status, etapa, score,
 * canal, parceiro e responsável não mudam — só o produto.
 */
export const ReclassificacaoProdutoCard: FC<Props> = ({ oportunidade, podeReclassificar, onReclassificada }) => {
  const outra = reservaEhOutraNecessidade(oportunidade)
  const [historico, setHistorico] = useState<ReclassificacaoProduto[]>([])
  const [aberto, setAberto] = useState(false)
  const [produtos, setProdutos] = useState<Produto[] | null>(null)
  const [erroProdutos, setErroProdutos] = useState(false)
  const [produtoId, setProdutoId] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let ativo = true
    reclassificacaoService
      .listar(oportunidade.id)
      .then((linhas) => {
        if (ativo) setHistorico(Array.isArray(linhas) ? linhas : [])
      })
      .catch(() => {
        // Sem histórico (backend anterior à V60 ou falha): o card só não mostra a lista.
        if (ativo) setHistorico([])
      })
    return () => {
      ativo = false
    }
  }, [oportunidade.id, oportunidade.produtoId])

  const statusPermite = !!oportunidade.status && STATUS_RECLASSIFICAVEIS.has(oportunidade.status)
  const destinos = useMemo(() => (produtos ?? []).filter(destinoValido), [produtos])

  const abrir = () => {
    setAberto(true)
    setErro(null)
    setProdutoId('')
    setMotivo('')
    if (produtos === null) {
      setErroProdutos(false)
      produtoService
        .listar(0, 200, { apenasAtivos: true })
        .then((pg) => setProdutos(pg.content ?? []))
        .catch(() => {
          setErroProdutos(true)
          setProdutos([])
        })
    }
  }

  const motivoLimpo = motivo.trim()
  const podeConfirmar =
    !!produtoId && motivoLimpo.length > 0 && motivoLimpo.length <= TAMANHO_MAXIMO_MOTIVO_RECLASSIFICACAO && !salvando

  const confirmar = async () => {
    if (!podeConfirmar) return
    setSalvando(true)
    setErro(null)
    try {
      const atualizada = await reclassificacaoService.reclassificar(oportunidade.id, {
        produtoId,
        motivo: motivoLimpo,
      })
      setAberto(false)
      onReclassificada(atualizada)
    } catch (e) {
      setErro(mensagemErroReclassificacao(e))
    } finally {
      setSalvando(false)
    }
  }

  if (!outra && historico.length === 0) return null

  return (
    <Card sx={{ mb: 3 }} component="section" aria-label="Produto da reserva">
      <CardHeader
        title="Produto da reserva"
        subheader={outra ? `${ROTULO_OUTRA_NECESSIDADE} · ${oportunidade.tipoNecessidade || 'sem descrição'}` : undefined}
      />
      <CardContent>
        {outra && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: podeReclassificar ? 1.5 : 0 }}>
            Sem elegibilidade, checklist nem simulação automática — a Originei avalia a necessidade descrita.
          </Typography>
        )}
        {outra && podeReclassificar && (
          <Box sx={{ mb: historico.length > 0 ? 2 : 0 }}>
            <Button variant="outlined" size="small" onClick={abrir} disabled={!statusPermite}>
              Reclassificar produto
            </Button>
            {!statusPermite && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Disponível de “Aguardando aprovação” até “Aprovada Originei”
                {oportunidade.status ? ` (status atual: ${STATUS_LABEL[oportunidade.status as StatusReserva] ?? oportunidade.status})` : ''}.
              </Typography>
            )}
          </Box>
        )}
        {historico.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Reclassificações
            </Typography>
            <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Histórico de reclassificações">
              {historico.map((h) => (
                <Box component="li" key={h.id}>
                  <Typography variant="body2">
                    {ROTULO_OUTRA_NECESSIDADE} ({h.tipoNecessidadeAnterior}) → <strong>{h.produtoNovoNome}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatDateTime(h.ocorridoEm)}
                    {h.autorNome ? ` · por ${h.autorNome}` : ''} · Motivo: {h.motivo}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>

      <Dialog open={aberto} onClose={() => !salvando && setAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reclassificar produto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A reserva deixa de ser “{ROTULO_OUTRA_NECESSIDADE}” e passa ao produto escolhido. Status, etapa,
              parceiro e responsável não mudam; o checklist e o formulário passam a ser os do produto novo.
            </Typography>
            {produtos === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} aria-label="Carregando produtos" />
              </Box>
            ) : erroProdutos ? (
              <Alert severity="error">Não foi possível carregar o catálogo de produtos.</Alert>
            ) : (
              <TextField
                select
                label="Produto"
                value={produtoId}
                onChange={(e) => setProdutoId(e.target.value)}
                fullWidth
                required
              >
                {destinos.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nome}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              inputProps={{ maxLength: TAMANHO_MAXIMO_MOTIVO_RECLASSIFICACAO }}
              helperText={`${motivo.length}/${TAMANHO_MAXIMO_MOTIVO_RECLASSIFICACAO}`}
            />
            {erro && (
              <Alert severity="error" role="alert">
                {erro}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void confirmar()} disabled={!podeConfirmar}>
            {salvando ? 'Reclassificando…' : 'Reclassificar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default ReclassificacaoProdutoCard
